import { useEffect, useState, useRef, useMemo } from "react";
import { PLOTS } from "../config/plots";
import { getMeasureTags, getMeasureOwner } from "../config/plotGroups";

interface SearchPopupProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTag?: string | null;
  selectedOwner?: string | null;
  outlierOnly?: boolean;
  outlierPlotIds?: Set<string>;
}

export default function SearchPopup({
  isOpen,
  onClose,
  selectedTag,
  selectedOwner,
  outlierOnly,
  outlierPlotIds,
}: SearchPopupProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Filter plots based on active filters and search query
  const filteredPlots = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return PLOTS.filter(plot => {
      if (selectedTag && !getMeasureTags(plot.measureId).includes(selectedTag)) return false;
      if (selectedOwner && getMeasureOwner(plot.measureId) !== selectedOwner) return false;
      if (outlierOnly && outlierPlotIds && !outlierPlotIds.has(plot.id)) return false;
      if (query && !plot.label.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [searchQuery, selectedTag, selectedOwner, outlierOnly, outlierPlotIds]);

  // Reset selected index when filtered plots change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredPlots]);

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setSearchQuery("");
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredPlots.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filteredPlots.length > 0) {
          jumpToChart(filteredPlots[selectedIndex].id);
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, filteredPlots, selectedIndex]);

  const jumpToChart = (plotId: string) => {
    // Find the chart element by its ID
    const chartElement = document.getElementById(`chart-${plotId}`);

    console.log("Jumping to chart:", plotId, chartElement);

    if (chartElement) {
      // Scroll to the chart with smooth behavior
      chartElement.scrollIntoView();

      // Add a small offset to account for sticky header
      setTimeout(() => {
        window.scrollBy({ top: -100, behavior: "smooth" });
      }, 100);
    }
  };

  const handleItemClick = (plotId: string) => {
    jumpToChart(plotId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="search-popup-overlay" onClick={onClose}>
      <div className="search-popup" onClick={e => e.stopPropagation()}>
        <div className="search-popup__header">
          <input
            ref={inputRef}
            type="text"
            className="search-popup__input"
            placeholder="Search charts..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="search-popup__results">
          {filteredPlots.length === 0 ? (
            <div className="search-popup__no-results">No charts found</div>
          ) : (
            <ul className="search-popup__list">
              {filteredPlots.map((plot, index) => (
                <li
                  key={plot.id}
                  className={`search-popup__item ${index === selectedIndex ? "search-popup__item--selected" : ""}`}
                  onClick={() => handleItemClick(plot.id)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {plot.label}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="search-popup__footer">
          <span className="search-popup__hint">
            <kbd>↑↓</kbd> Navigate <kbd>↵</kbd> Select <kbd>Esc</kbd> Close
          </span>
        </div>
      </div>
    </div>
  );
}
